import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useUploadFile } from '@/hooks/use-upload-file';
import { LAYER_IMAGE_MIME_TYPES, isAcceptedMimeType } from '@/lib/upload/acceptedMimeTypes';
import { MAX_APPEAL_EVIDENCE_IMAGES } from '@/lib/campaigns/appealEvidenceImages';

type AppealContestDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  originalReason: string;
  auditorReply?: string;
  mode?: 'contest' | 'followUp';
  onSubmit: (input: {
    argument: string;
    extraUrl: string;
    evidenceImageUrls: string[];
  }) => void;
  loading?: boolean;
};

export function AppealContestDialog({
  open,
  onOpenChange,
  originalReason,
  auditorReply,
  mode = 'contest',
  onSubmit,
  loading = false,
}: AppealContestDialogProps) {
  const { t } = useTranslation();
  const { getUploadUrl } = useUploadFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [argument, setArgument] = useState('');
  const [extraUrl, setExtraUrl] = useState('');
  const [evidenceImageUrls, setEvidenceImageUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setArgument('');
    setExtraUrl('');
    setEvidenceImageUrls([]);
    setUploading(false);
    setUploadError(null);
  }, [open]);

  const busy = loading || uploading;
  const canSubmit = argument.trim().length > 0 && !busy;
  const canAddMore = evidenceImageUrls.length < MAX_APPEAL_EVIDENCE_IMAGES && !busy;

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = MAX_APPEAL_EVIDENCE_IMAGES - evidenceImageUrls.length;
    if (remaining <= 0) return;

    const selected = Array.from(files)
      .filter((file) => isAcceptedMimeType(file.type, LAYER_IMAGE_MIME_TYPES))
      .slice(0, remaining);
    if (selected.length === 0) {
      setUploadError(t('campaign.submissions.appeal.photosInvalidType'));
      return;
    }

    setUploadError(null);
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of selected) {
        const url = await getUploadUrl(file, undefined, {
          acceptedMimeTypes: LAYER_IMAGE_MIME_TYPES,
        });
        if (!url) {
          setUploadError(t('campaign.submissions.appeal.photosUploadFailed'));
          break;
        }
        uploaded.push(url);
      }
      if (uploaded.length > 0) {
        setEvidenceImageUrls((prev) => [...prev, ...uploaded].slice(0, MAX_APPEAL_EVIDENCE_IMAGES));
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      argument: argument.trim(),
      extraUrl: extraUrl.trim(),
      evidenceImageUrls,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>
              {t(mode === 'followUp'
                ? 'campaign.submissions.appeal.followUpTitle'
                : 'campaign.submissions.appeal.dialogTitle')}
            </DialogTitle>
            <DialogDescription>
              {t(mode === 'followUp'
                ? 'campaign.submissions.appeal.followUpHint'
                : 'campaign.submissions.appeal.dialogHint')}
            </DialogDescription>
          </DialogHeader>

          {originalReason ? (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm leading-snug text-muted-foreground whitespace-pre-line break-words">
              {originalReason}
            </p>
          ) : null}

          {auditorReply ? (
            <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm leading-snug text-amber-200 whitespace-pre-line break-words">
              <span className="text-muted-foreground">
                {t('campaign.submissions.appeal.keptReplyLabel')}
              </span>
              {' '}
              {auditorReply}
            </p>
          ) : null}

          <div className="grid gap-2">
            <Label htmlFor="appeal-argument">
              {t('campaign.submissions.appeal.argumentLabel')}
            </Label>
            <Textarea
              id="appeal-argument"
              value={argument}
              onChange={(event) => setArgument(event.target.value)}
              disabled={busy}
              required
              className="min-h-[120px]"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="appeal-extra-url">
              {t('campaign.submissions.appeal.extraUrlLabel')}
            </Label>
            <Input
              id="appeal-extra-url"
              type="text"
              value={extraUrl}
              onChange={(event) => setExtraUrl(event.target.value)}
              disabled={busy}
            />
          </div>

          <div className="grid gap-2">
            <Label>{t('campaign.submissions.appeal.photosLabel')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('campaign.submissions.appeal.photosHint', { max: MAX_APPEAL_EVIDENCE_IMAGES })}
            </p>
            <div className="flex flex-wrap gap-2">
              {evidenceImageUrls.map((url) => (
                <div
                  key={url}
                  className="relative h-20 w-20 overflow-hidden rounded-md border border-border bg-muted"
                >
                  <img src={url} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    className="absolute right-1 top-1 rounded-full bg-background/90 p-0.5 text-foreground shadow"
                    disabled={busy}
                    aria-label={t('campaign.submissions.appeal.photosRemove')}
                    onClick={() => {
                      setEvidenceImageUrls((prev) => prev.filter((item) => item !== url));
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {canAddMore ? (
                <button
                  type="button"
                  className="flex h-20 w-20 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground hover:bg-muted/40"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                  data-testid="appeal-add-photos"
                >
                  {uploading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Plus className="h-5 w-5" />
                  )}
                </button>
              ) : null}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={LAYER_IMAGE_MIME_TYPES.join(',')}
              multiple
              className="hidden"
              onChange={(event) => {
                void handleFiles(event.target.files);
              }}
            />
            {uploadError ? (
              <p className="text-xs text-destructive">{uploadError}</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="submit" disabled={!canSubmit} data-testid="appeal-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t(mode === 'followUp'
                ? 'campaign.submissions.appeal.followUpSubmit'
                : 'campaign.submissions.appeal.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
